import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import {
  BookingServiceRequestInfo,
  BookingTravelerInfo,
  PVIGetPlanRequest,
  PviInsuranceInventoryResourceService,
  PviInsurancePlanDTO,
  PviInsuranceResourceService,
  PVIInsurerRQ,
  PVIOrderRequest,
  PVIPlanPerson
} from '@gtd/api-services/service-inventory';
import { DateUtils } from '@gtd/utilities';
import { BehaviorSubject } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

export interface IPlanSelected {
  planType: string;
  insurance: PviInsurancePlanDTO;
}
@Injectable({
  providedIn: 'root'
}) //TODO: update type safety
export class InsuranceService {
  paxInfosSubject = new BehaviorSubject<any[]>([]);
  paxInfos$ = this.paxInfosSubject.asObservable();
  holderIndexSubject = new BehaviorSubject<number>(null);
  holderIndex$ = this.holderIndexSubject.asObservable();
  planSelectedSubject = new BehaviorSubject<IPlanSelected>(null);
  planSelected$ = this.planSelectedSubject.asObservable();
  insurersSubject = new BehaviorSubject<any[]>(null);
  insurers$ = this.insurersSubject.asObservable();
  checked = false;
  domesticPlans = [
    { label: 'Chương trình A3', value: 'DOM_A3' },
    { label: 'Chương trình A2', value: 'DOM_A2' },
    { label: 'Chương trình A1', value: 'DOM_A1' },
    { label: 'Chương trình D3', value: 'DOM_D3' },
    { label: 'Chương trình D2', value: 'DOM_D2' },
    { label: 'Chương trình D1', value: 'DOM_D1' }
  ];
  internationalPlans = [
    { label: 'Gói cao cấp', value: 'PREMIUM' },
    { label: 'Gói phổ thông', value: 'NORMAL' },
    { label: 'Gói cơ bản', value: 'BASIC' }
  ];
  constructor(
    private pviResourceService: PviInsuranceResourceService,
    private pviInventoryResourceService: PviInsuranceInventoryResourceService,
    private datePipe: DatePipe
  ) {}

  getPlanLabel(type: 'DOME' | 'INTE', value: string) {
    const plans = type == 'DOME' ? this.domesticPlans : this.internationalPlans;
    const plan = plans.find(plan => plan.value === value);
    return plan.label
      .replace('Chương trình ', '')
      .replace('Gói ', '')
      .replace(/^\w/, c => c.toUpperCase());
  }

  setPaxInfos(paxInfos: any) {
    this.paxInfosSubject.next(paxInfos);
  }

  setHolderIndex(index: number) {
    this.holderIndexSubject.next(index);
  }

  setPlanSelected(planType: string, insurancePlan: PviInsurancePlanDTO) {
    this.planSelectedSubject.next({ planType, insurance: insurancePlan });
  }

  resetPlanSelected() {
    this.planSelectedSubject.next(null);
  }

  setInsurer(insurers: any[]) {
    this.insurersSubject.next(insurers);
  }

  getPlans(planReq: PVIGetPlanRequest) {
    return this.pviInventoryResourceService.getPlansByPVIUsingPOST(planReq);
  }

  createPviInsuranceOrder(bookingNumber: string) {
    return this.planSelected$.pipe(
      filter(plan => plan != null),
      switchMap(plan =>
        this.insurers$.pipe(
          map(insurers => ({ insurers, plan })),
          switchMap(insurerMetadata => {
            const { insurers, plan } = insurerMetadata;
            const insurerRQS: PVIInsurerRQ[] = insurers.map(insurer => ({
              address: insurer.address,
              birthDate: this.datePipe.transform(
                insurer.birthDay,
                'dd/MM/yyyy'
              ),
              fullName: insurer.fullName,
              gender: insurer.gender,
              isHolder: insurer.isHolder,
              identityDocument: insurer.identityDocument,
              phoneNumber: insurer.phoneNumber
            }));
            const holder = insurers.find(insurer => insurer.isHolder);
            const orderReq: PVIOrderRequest = {
              bookingNumber,
              holderEmail: holder.email,
              insureAmount: plan.insurance.insureAmount,
              premiumRate: plan.insurance.premiumRate,
              totalChargedAmount: plan.insurance.totalChargedAmount,
              insurerRQS
            };
            return this.pviResourceService.createOrderUsingPOST(orderReq);
          })
        )
      )
    );
  }

  getPviInsuranceOrder(transId: string) {
    return this.pviInventoryResourceService.getOrderUsingGET(transId);
  }

  issuePviInsuranceOrder(transId: string) {
    return this.pviResourceService.issueOrderUsingPOST({
      transactionId: transId
    });
  }

  mapToPersonalInfo(paxInfos: any[], toDate?: string | Date) {
    return paxInfos.map(
      pax =>
        ({
          age: DateUtils.calculateAge(pax.birthDay, toDate)
        } as PVIPlanPerson)
    );
    // .filter(paxInfo => paxInfo.age > 1);
  }

  getTransactionIdFromTravellerInfos(
    travelerInfos: BookingTravelerInfo[]
  ): string | null {
    for (const traveler of travelerInfos) {
      if (traveler.serviceRequests) {
        for (const request of traveler.serviceRequests) {
          if (
            request.serviceType ===
            BookingServiceRequestInfo.ServiceTypeEnum.PVI
          ) {
            return request.ssrId;
          }
        }
      }
    }
    return null;
  }

  reset() {
    this.insurersSubject.next(null);
    this.holderIndexSubject.next(null);
    this.planSelectedSubject.next(null);
    this.holderIndexSubject.next(null);
    this.paxInfosSubject.next(null);
    this.checked = false;
  }
}
